import Tabs, { TabPane } from 'rc-tabs';
import React from 'react';
import { SkeletonLoading } from '@saleshandy/design-system';
import { Images } from '../../../../../../shared/app-constants';
import { AvatarWithName } from '../../../../../../shared/design-system/components/atoms/avatar-with-name/avatar-with-name';
import Table from '../../../../../../shared/design-system/components/organisms/table';
import {
  Column,
  PaginationShowHide,
} from '../../../../../../shared/design-system/components/organisms/table/types';
import { PaginationQueryParams } from '../../../../../../shared/types/request';
import { CallOutcome } from '../../types';
import {
  generateCallOutcomeActionsColumn,
  getCallOutcomeColumns,
  getCallOutcomeTableWrapper,
} from './helpers/call-outcome-column-data';
import { CallOutcomeContentTabs } from '../../enums';
import SearchInput from '../../../../../../shared/design-system/components/atoms/search-input';
import { IconPlace } from '../../../../../../shared/design-system/components/input/enums';
import Button from '../../../../../../shared/design-system/components/atoms/button';
import Icon from '../../../../../../shared/design-system/components/atoms/icon';
import ImageIcon from '../../../../../../shared/components/images/image-icon';
import hasPermission from '../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../shared/utils/access-control/enums/permissions';
import { supportUrls } from '../../../../../../shared/utils/urls';

type CallOutcomeTabPanesProps = {
  customOutcomes: CallOutcome[];
  defaultOutcomes: CallOutcome[];
  onCustomOutcomePaginationOptionsChangeHandler: (
    newPartialOptions: Partial<PaginationQueryParams>,
  ) => void;
  onDefaultOutcomePaginationOptionsChangeHandler: (
    newPartialOptions: Partial<PaginationQueryParams>,
  ) => void;
  customOutcomePagination: {
    options: Required<PaginationQueryParams> & {
      totalElements: number;
      search?: string;
    };
    pagesCount: number;
    count: number;
  };
  defaultOutcomePagination: {
    options: Required<PaginationQueryParams> & {
      totalElements: number;
      search?: string;
    };
    pagesCount: number;
    count: number;
  };
  onActionHandler: (key: string, row: CallOutcome) => void;
  activeTabKey: string;
  onTabChange: (tabKey: string) => void;
  onCreateHandler: (event: React.MouseEvent<HTMLElement>) => void;
  search: string;
  setSearch: (search: string) => void;
  onClearSearch: () => void;
  onSearch: () => void;
  isRequestPending: boolean;
};

const CallOutcomeTabPanes: React.FC<CallOutcomeTabPanesProps> = ({
  customOutcomes,
  defaultOutcomes,
  onDefaultOutcomePaginationOptionsChangeHandler,
  onCustomOutcomePaginationOptionsChangeHandler,
  customOutcomePagination,
  defaultOutcomePagination,
  onActionHandler,
  activeTabKey,
  onTabChange,
  onCreateHandler,
  search,
  setSearch,
  onClearSearch,
  onSearch,
  isRequestPending,
}) => {
  const renderTable = (tab: CallOutcomeContentTabs, columns: Column[]) => {
    if (
      customOutcomes.length === 0 &&
      tab === CallOutcomeContentTabs.CustomOutcomes &&
      !isRequestPending
    ) {
      return (
        <div className="call-outcome-empty-state">
          <div className="call-outcome-empty-inner">
            <div className="call-outcome-empty-image">
              <ImageIcon src={Images.EmptyData7} alt="empty-data/" />
            </div>
            <div className="call-outcome-empty-title">
              Create custom outcomes
            </div>
            <p className="call-outcome-empty-description">
              Start organizing your calls with custom outcomes.{' '}
              <a
                href={supportUrls.dialerCallOutcomeLearnMoreLink}
                target="_blank"
                rel="noreferrer"
              >
                Learn More
              </a>
            </p>
          </div>
        </div>
      );
    }
    return (
      <Table
        actions={generateCallOutcomeActionsColumn(tab)}
        newThemeActions={true}
        columns={columns}
        data={
          tab === CallOutcomeContentTabs.CustomOutcomes
            ? customOutcomes
            : defaultOutcomes
        }
        onAction={onActionHandler}
        paginationOptions={
          tab === CallOutcomeContentTabs.CustomOutcomes
            ? customOutcomePagination
            : defaultOutcomePagination
        }
        onPaginationOptionsChange={
          tab === CallOutcomeContentTabs.CustomOutcomes
            ? onCustomOutcomePaginationOptionsChangeHandler
            : onDefaultOutcomePaginationOptionsChangeHandler
        }
        headerVisibleForGenerateColumn
        pagination={PaginationShowHide.SHOW}
        hidePageOptions
        tableWrapperClasses={getCallOutcomeTableWrapper(
          tab,
          customOutcomePagination.count,
          defaultOutcomePagination.count,
        )}
        isNewPagination={true}
        isLoading={isRequestPending}
        customPaginationOptions={[
          { key: '25', value: 25 },
          { key: '50', value: 50 },
          { key: '100', value: 100 },
        ]}
      />
    );
  };

  return (
    <Tabs
      activeKey={activeTabKey}
      prefixCls="bs-tabs"
      className="bs-tabs-small"
      onChange={(tabKey) => onTabChange(tabKey)}
      tabBarExtraContent={
        activeTabKey === CallOutcomeContentTabs.CustomOutcomes && (
          <div className="call-outcome-actions-bar">
            <div className="d-flex align-items-center">
              <SearchInput
                searchValue={search}
                onSearch={onSearch}
                onClearSearch={onClearSearch}
                setSearchValue={setSearch}
                searchIconPlace={IconPlace.Left}
                placeholder="Search here..."
                initialValue={search}
                className="call-outcome-search"
              />
            </div>
            {hasPermission(Permissions.CALL_OUTCOME_WRITE) && (
              <Button
                onClick={onCreateHandler}
                variant={Button.Variant.Primary}
                className="call-outcome-add-btn"
                iconLeft={<Icon identifier="math-plus" />}
              >
                Custom Outcome
              </Button>
            )}
          </div>
        )
      }
    >
      {[
        CallOutcomeContentTabs.CustomOutcomes,
        CallOutcomeContentTabs.SystemOutcomes,
      ].map((tab) => {
        let columns: Column[] = getCallOutcomeColumns();

        if (tab === CallOutcomeContentTabs.CustomOutcomes) {
          columns = [
            ...columns,
            {
              dataField: 'user',
              text: 'Added By',
              headerAlign: 'left',
              align: 'left',
              component: (cell) => {
                if (cell) {
                  const { firstName, lastName } = cell;
                  return (
                    <AvatarWithName firstName={firstName} lastName={lastName} />
                  );
                }
                return '-';
              },
              headerLoadingSkeleton: <SkeletonLoading width={34} height={14} />,
              cellLoadingSkeleton: <SkeletonLoading width={110} height={14} />,
            },
          ];
        }

        return (
          <TabPane
            tab={
              tab === CallOutcomeContentTabs.CustomOutcomes
                ? 'Custom Outcome'
                : 'System Outcome'
            }
            key={tab}
          >
            <div className="call-outcome-content-container__tab-content table-wrapper">
              {renderTable(tab, columns)}
            </div>
          </TabPane>
        );
      })}
    </Tabs>
  );
};

export default CallOutcomeTabPanes;
